/**
 * The engine the shell and the tools share (plan Architecture items 1-6): lane A's song store,
 * audio context, transport, metronome, reconciler and export jobs, wired to lane B's recorder,
 * played-note input and take requests. Tools reach audio only through this object; the document is
 * the authority and the graph is reconciled from it.
 */
import { createAudioContextManager, type AudioContextManager } from '../audio/context.ts';
import { encodeMidi } from '../audio/encoders/midi.ts';
import { encodeMp3 } from '../audio/encoders/mp3.ts';
import { encodeWav } from '../audio/encoders/wav.ts';
import {
  INSTRUMENT_CATALOGUE,
  loadInstrument,
  type AudioInstrument,
} from '../audio/instruments.ts';
import { ExportJobManager, type ExportJob } from '../audio/jobs.ts';
import { createMetronome, type Metronome } from '../audio/metronome.ts';
import { createAudioReconciler, type AudioReconciler } from '../audio/reconciler.ts';
import { analyseAudioBuffer, type LoudnessReading } from '../audio/loudness.ts';
import { getRenderFallbacks, renderSong } from '../audio/render.ts';
import {
  countInStartBar,
  createSongTransport,
  createTakeBackingSong,
  type PlayOptions,
  type SongTransport,
} from '../audio/transport.ts';
import { importAudioFile, type ImportedAudio } from '../input/importFile.ts';
import { PlayedNoteRecorder, type PlayedNoteSink } from '../input/musicalTyping.ts';
import {
  RecorderController,
  type RecordedTake,
  type RecorderResult,
  type RecorderSnapshot,
  type StartRecordingOptions,
} from '../input/recorder.ts';
import { armedTakeRequestFromSong, type ArmedTakeRequest } from '../input/requestTake.ts';
import type { RecorderAudioContext, TransportPort } from '../input/transportPort.ts';
import { createSongReducer } from '../song/reducer.ts';
import {
  createSongPersistence,
  loadExampleSong,
  loadSong,
  type SongPersistence,
} from '../song/serialise.ts';
import type { SongStateContext } from '../song/selectors.ts';
import { createSongStore, type SongStore } from '../song/store.ts';
import {
  createEmptySong,
  type SongDocument,
  type Take,
  type TeachingOption,
  type TeachingOptionSet,
} from '../song/types.ts';
import { createCaptureReducer } from './captureReducer.ts';
import { ToolError } from './envelope.ts';
import type { EnvironmentStore } from './environment.ts';
import { applyOptionPreview, createPlaybackView, type PlaybackView } from './preview.ts';

export type ExportFormat = 'wav' | 'mp3' | 'midi';

export interface ExportResult {
  download_url: string;
  filename: string;
  duration_s: number;
  peak_dbfs: number | null;
  bytes: number;
  fallbacks?: readonly string[] | undefined;
}

export interface RecorderPort {
  getSnapshot(): RecorderSnapshot;
  subscribe(listener: () => void): () => void;
  start(options: StartRecordingOptions): Promise<RecorderResult<RecorderSnapshot>>;
  stop(): Promise<RecorderResult<RecordedTake>>;
  dispose(): void;
}

export interface AuditionResult {
  set: TeachingOptionSet;
  option: TeachingOption;
}

export interface TakeBackingDiagnostics {
  startBar: number;
  trackId: string;
  mute: boolean;
  solo: boolean;
  silent: boolean;
}

export interface EngineSnapshot {
  audio: { state: string; running: boolean };
  playing: boolean;
  recording: RecorderSnapshot;
  takeBacking: TakeBackingDiagnostics | null;
  jobs: readonly ExportJob[];
  preview: { option_id: string; label: string } | null;
  loading: Record<string, number>;
  fallbacks: Record<string, string>;
}

export interface Exporters {
  render: typeof renderSong;
  wav: typeof encodeWav;
  mp3: typeof encodeMp3;
  midi: typeof encodeMidi;
}

export interface EngineOptions {
  document?: SongDocument | undefined;
  storage?: Pick<Storage, 'getItem' | 'setItem'> | null | undefined;
  environment?: EnvironmentStore | undefined;
  audio?: AudioContextManager | undefined;
  transport?: SongTransport | undefined;
  metronome?: Metronome | undefined;
  jobs?: ExportJobManager | undefined;
  recorder?: RecorderPort | undefined;
  keyboardInstrument?: string | null | undefined;
  createReconciler?:
    ((view: PlaybackView, audio: AudioContextManager) => AudioReconciler) | undefined;
  exporters?: Partial<Exporters> | undefined;
  createObjectUrl?: ((blob: Blob) => string) | undefined;
  revokeObjectUrl?: ((url: string) => void) | undefined;
  makeId?: ((prefix: string) => string) | undefined;
}

export interface Engine {
  readonly store: SongStore;
  readonly audio: AudioContextManager;
  readonly transport: SongTransport;
  readonly metronome: Metronome;
  readonly jobs: ExportJobManager;
  readonly recorder: RecorderPort;
  readonly keys: PlayedNoteRecorder;
  readonly playback: PlaybackView;
  readonly transportPort: TransportPort;
  /** Creates the audio context inside a click handler and resolves once it is running. */
  activate(): Promise<void>;
  reconciler(): AudioReconciler | null;
  recordingTrackId(): string | null;
  takeRequest(): ArmedTakeRequest | null;
  pendingTake(): Take | null;
  setPendingTake(takeId: string | null): void;
  addTake(take: Take, why: string, source: 'human' | 'agent'): void;
  makeId(prefix: string): string;
  play(options?: PlayOptions): Promise<{ playing: boolean; position_bar: number }>;
  stop(): Promise<{ playing: boolean; position_bar: number }>;
  audition(optionId: string): Promise<AuditionResult>;
  clearPreview(): void;
  startExport(format: ExportFormat, barFrom: number, barTo: number): ExportJob;
  exportResult(jobId: string): ExportResult | null;
  importFile(file: File): Promise<ImportedAudio>;
  loadExample(): void;
  newSong(): void;
  stateContext(): SongStateContext;
  getSnapshot(): EngineSnapshot;
  subscribe(listener: () => void): () => void;
  dispose(): void;
}

const KEYBOARD_NOTE_SECONDS = 1.2;

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted) throw signal.reason ?? new DOMException('Take stopped.', 'AbortError');
}

function waitWithSignal(promise: Promise<void>, signal: AbortSignal | undefined): Promise<void> {
  if (!signal) return promise;
  throwIfAborted(signal);
  return new Promise<void>((resolve, reject) => {
    const abort = (): void =>
      reject(signal.reason ?? new DOMException('Take stopped.', 'AbortError'));
    signal.addEventListener('abort', abort, { once: true });
    promise.then(
      () => {
        signal.removeEventListener('abort', abort);
        resolve();
      },
      (error: unknown) => {
        signal.removeEventListener('abort', abort);
        reject(error);
      },
    );
  });
}

/**
 * Builds the engine.
 *
 * @param options - Seams for tests; the defaults are the real audio stack.
 * @returns The engine, with no browser audio created until `activate` runs.
 */
export function createEngine(options: EngineOptions = {}): Engine {
  const makeId =
    options.makeId ?? ((prefix: string) => `${prefix}-${crypto.randomUUID().slice(0, 8)}`);
  const audio = options.audio ?? createAudioContextManager();
  const transport = options.transport ?? createSongTransport(audio);
  const metronome = options.metronome ?? createMetronome();
  const jobs = options.jobs ?? new ExportJobManager(() => makeId('job'));
  const exporters: Exporters = {
    render: options.exporters?.render ?? renderSong,
    wav: options.exporters?.wav ?? encodeWav,
    mp3: options.exporters?.mp3 ?? encodeMp3,
    midi: options.exporters?.midi ?? encodeMidi,
  };
  const createObjectUrl = options.createObjectUrl ?? ((blob: Blob) => URL.createObjectURL(blob));
  const revokeObjectUrl = options.revokeObjectUrl ?? ((url: string) => URL.revokeObjectURL(url));

  let recorderPort: RecorderPort | null = null;
  const recordingTrackId = (): string | null => {
    const snapshot = recorderPort?.getSnapshot();
    if (!snapshot) return null;
    return snapshot.status === 'recording' || snapshot.status === 'counting-in'
      ? snapshot.trackId
      : null;
  };

  const initial =
    options.document ?? (options.storage ? loadSong(options.storage) : null) ?? createEmptySong();
  const store = createSongStore(
    initial,
    createCaptureReducer(createSongReducer({ recordingTrackId, idFactory: makeId })),
  );
  const playback = createPlaybackView(store);
  let takeBacking: TakeBackingDiagnostics | null = null;

  const transportPort: TransportPort = {
    getAudioContext: () => audio.getContext() as RecorderAudioContext | null,
    getBpm: () => store.getDocument().bpm,
    getTimeSignature: () => store.getDocument().time_sig,
    getPositionSeconds: () => audio.getContext()?.currentTime ?? 0,
    async countIn({ bars, metronome: click, targetBar, mutedTrackId, signal }) {
      throwIfAborted(signal);
      if (targetBar !== undefined && mutedTrackId !== undefined) {
        takeBacking = null;
        notify();
      }
      const song = store.getDocument();
      const bpm = song.bpm;
      const beatsPerBar = song.time_sig[0];
      const durationSeconds = (bars * beatsPerBar * 60) / bpm;
      const backing =
        targetBar === undefined || mutedTrackId === undefined
          ? null
          : createTakeBackingSong(song, mutedTrackId);
      const hasFullBackingPreRoll = backing !== null && targetBar !== undefined && targetBar > bars;
      const captureBar = targetBar ?? 1;
      const startBar =
        targetBar === undefined || !hasFullBackingPreRoll ? 1 : countInStartBar(targetBar, bars);
      const silentPreRoll =
        backing === null || hasFullBackingPreRoll
          ? null
          : {
              ...backing,
              tracks: backing.tracks.map((track) => ({ ...track, mute: true, solo: false })),
            };
      let finished = false;
      const finish = (): void => {
        if (finished) return;
        finished = true;
        metronome.stop();
        void transport.stop().catch(() => undefined);
        const activePreview = playback.getPreview();
        if (activePreview === backing || activePreview === silentPreRoll) playback.setPreview(null);
        notify();
      };
      const ensureActive = async (): Promise<void> => {
        if (!finished) return;
        metronome.stop();
        await transport.stop().catch(() => undefined);
        throw signal?.reason ?? new DOMException('Take stopped.', 'AbortError');
      };
      const startBacking = async (document: SongDocument, fromBar: number): Promise<void> => {
        await transport.play(document, { from_bar: fromBar });
        const target = document.tracks.find(({ id }) => id === mutedTrackId);
        if (target !== undefined) {
          const anotherTrackIsSoloed = document.tracks.some(
            (track) => track.id !== target.id && track.solo,
          );
          takeBacking = {
            startBar: fromBar,
            trackId: target.id,
            mute: target.mute,
            solo: target.solo,
            silent: target.mute || (anotherTrackIsSoloed && !target.solo),
          };
          notify();
        }
        await ensureActive();
      };
      const abort = (): void => finish();
      signal?.addEventListener('abort', abort, { once: true });
      try {
        let recordingStartContextTime: number | null = null;
        let complete: (time: number) => void = () => undefined;
        const completed = new Promise<void>((resolve) => {
          complete = (time) => {
            recordingStartContextTime = time;
            resolve();
          };
        });
        if (silentPreRoll !== null) playback.setPreview(silentPreRoll);
        await metronome.scheduleCountIn({
          bars,
          bpm,
          beatsPerBar,
          startBar,
          startTransport: backing === null || !hasFullBackingPreRoll,
          audible: click,
          continueClick: click && (backing === null || hasFullBackingPreRoll),
          onComplete: complete,
        });
        await ensureActive();
        if (backing !== null && hasFullBackingPreRoll) {
          playback.setPreview(backing);
          await startBacking(backing, startBar);
        }
        await waitWithSignal(completed, signal);
        if (backing !== null && !hasFullBackingPreRoll) {
          metronome.stop();
          playback.setPreview(backing);
          await startBacking(backing, captureBar);
          recordingStartContextTime = audio.requireRunning().currentTime;
        }
        throwIfAborted(signal);
        if (recordingStartContextTime === null) {
          throw new Error('The count-in clock did not report its recording boundary.');
        }
        return { durationSeconds, recordingStartContextTime, finish };
      } catch (error) {
        finish();
        throw error;
      } finally {
        signal?.removeEventListener('abort', abort);
      }
    },
  };

  let keyboardInstrument: AudioInstrument | null = null;
  const keyboardSink: PlayedNoteSink = {
    noteOn(pitch, velocity) {
      const context = audio.getContext();
      if (!context || keyboardInstrument === null) return;
      keyboardInstrument.trigger(pitch, context.currentTime, KEYBOARD_NOTE_SECONDS, velocity);
    },
    noteOff() {
      /* The sampled keyboard voices decay on their own; there is no note-off to send. */
    },
  };
  const keys = new PlayedNoteRecorder(transportPort, keyboardSink);
  const recorder = options.recorder ?? new RecorderController(transportPort);
  recorderPort = recorder;

  const persistence: SongPersistence | null = options.storage
    ? createSongPersistence(store, options.storage)
    : null;

  let reconciler: AudioReconciler | null = null;
  let stopReconciler: (() => void) | null = null;
  let activation: Promise<void> | null = null;
  let previewSequence = 0;
  let preview: { option_id: string; label: string } | null = null;
  let pendingTakeId: string | null = null;
  let snapshot: EngineSnapshot | null = null;
  const listeners = new Set<() => void>();
  const notify = (): void => {
    snapshot = null;
    for (const listener of listeners) listener();
  };
  const stopJobs = jobs.subscribe(() => notify());
  const stopRecorder = recorder.subscribe(() => notify());
  const stopAudio = audio.subscribe(() => notify());

  const startInstrument = async (): Promise<void> => {
    const id =
      options.keyboardInstrument === undefined ? 'grand-piano' : options.keyboardInstrument;
    if (id === null || keyboardInstrument !== null) return;
    const context = audio.getContext();
    if (!context) return;
    const loaded = await loadInstrument(id, { context, destination: null });
    keyboardInstrument = loaded.instrument;
    notify();
  };

  const activate = (): Promise<void> => {
    if (activation) return activation;
    // The reconciler is built only after `activateFromGesture` resolves, because that is where
    // lane A hands our AudioContext to Tone. Building it earlier is a race: on a gesture the
    // context is already running, so the reconciler would ask Tone for a destination before
    // `setContext`, Tone would answer from its own default context, and smplr's native nodes
    // could not connect to it ("Overload resolution failed"). Measured in Chrome 151, 27 Aug.
    const running = audio.activateFromGesture();
    activation = running.then(async () => {
      if (reconciler === null) {
        reconciler = (options.createReconciler ?? createAudioReconciler)(playback, audio);
        stopReconciler = reconciler.subscribe(notify);
      }
      reconciler.reconcile();
      await startInstrument().catch(() => undefined);
      notify();
    });
    return activation;
  };

  const requireOption = (optionId: string): AuditionResult => {
    const song = store.getDocument();
    const set = song.option_sets.find((candidate) =>
      candidate.options.some(({ id }) => id === optionId),
    );
    const option = set?.options.find(({ id }) => id === optionId);
    if (!set || !option) {
      throw new ToolError(
        'INVALID_ARGUMENT',
        `Option "${optionId}" does not exist. Read get_song_state for the option ids.`,
        true,
      );
    }
    return { set, option };
  };

  /**
   * Final encoding-boundary defence for injected exporters. The real offline render already owns
   * the same peak ceiling; measured on 27 Aug the earlier path reached +6.6 dBFS.
   *
   * @param buffer - The rendered audio, scaled in place when it is over full scale.
   * @returns The loudness reading after any scaling.
   */
  const trimClipping = (buffer: AudioBuffer): LoudnessReading => {
    const before = analyseAudioBuffer(buffer);
    if (before.peak <= 1) return before;
    const gain = 0.98 / before.peak;
    for (let channel = 0; channel < buffer.numberOfChannels; channel += 1) {
      const data = buffer.getChannelData(channel);
      for (let index = 0; index < data.length; index += 1) data[index] = (data[index] ?? 0) * gain;
    }
    return analyseAudioBuffer(buffer);
  };

  const clearPreview = (): void => {
    if (preview === null) return;
    preview = null;
    playback.setPreview(null);
    notify();
  };

  const replaceSong = (document: SongDocument, summary: string): void => {
    const before = store.getDocument();
    const result = store.dispatch({
      type: '__restore_snapshot',
      args: { document, summary },
      source: 'human',
    });
    store.history.record(before, store.getDocument(), result.summary);
    pendingTakeId = null;
    clearPreview();
    notify();
  };

  const exports = new Map<string, ExportResult>();

  const runExport = async (
    format: ExportFormat,
    barFrom: number,
    barTo: number,
    signal: AbortSignal,
    setProgress: (value: number) => void,
  ): Promise<ExportResult> => {
    const song = store.getDocument();
    const name = `${
      song.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/gu, '-')
        .replace(/^-|-$/gu, '') || 'euterpe'
    }`;
    if (format === 'midi') {
      const bytes = exporters.midi(song, { start_bar: barFrom, end_bar: barTo });
      setProgress(90);
      return {
        download_url: createObjectUrl(new Blob([bytes as BlobPart], { type: 'audio/midi' })),
        filename: `${name}.mid`,
        duration_s: ((barTo - barFrom + 1) * song.time_sig[0] * 60) / song.bpm,
        peak_dbfs: null,
        bytes: bytes.length,
      };
    }
    const buffer = await exporters.render(
      song,
      { start_bar: barFrom, end_bar: barTo },
      { signal, onProgress: (value) => setProgress(value * 0.7) },
    );
    setProgress(75);
    const fallbacks = getRenderFallbacks(buffer);
    const loudness = trimClipping(buffer);
    const bytes =
      format === 'wav' ? exporters.wav(buffer) : await exporters.mp3(buffer, { signal });
    setProgress(95);
    return {
      download_url: createObjectUrl(
        new Blob([bytes as BlobPart], { type: format === 'wav' ? 'audio/wav' : 'audio/mpeg' }),
      ),
      filename: `${name}.${format === 'wav' ? 'wav' : 'mp3'}`,
      duration_s: Math.round(buffer.duration * 100) / 100,
      peak_dbfs: Number.isFinite(loudness.peak_dbfs) ? loudness.peak_dbfs : null,
      bytes: bytes.length,
      ...(fallbacks.length === 0 ? {} : { fallbacks }),
    };
  };

  return {
    store,
    audio,
    transport,
    metronome,
    jobs,
    recorder,
    keys,
    playback,
    transportPort,
    activate,
    reconciler: () => reconciler,
    recordingTrackId,
    takeRequest() {
      const request = store.getDocument().take_request;
      return request === null ? null : armedTakeRequestFromSong(request);
    },
    pendingTake() {
      if (pendingTakeId === null) return null;
      return store.getDocument().takes.find(({ id }) => id === pendingTakeId) ?? null;
    },
    setPendingTake(takeId) {
      pendingTakeId = takeId;
      notify();
    },
    addTake(take, why, source) {
      store.dispatch({ type: 'add_take', args: { take }, source, why });
      pendingTakeId = take.id;
      notify();
    },
    makeId,
    async play(playOptions = {}) {
      audio.requireRunning();
      clearPreview();
      const result = await transport.play(store.getDocument(), playOptions);
      notify();
      return { playing: result.playing, position_bar: result.position_bar };
    },
    async stop() {
      const result = await transport.stop();
      clearPreview();
      notify();
      return { playing: result.playing, position_bar: result.position_bar };
    },
    async audition(optionId) {
      const { set, option } = requireOption(optionId);
      audio.requireRunning();
      previewSequence += 1;
      const document = applyOptionPreview(store.getDocument(), set, option, previewSequence);
      playback.setPreview(document);
      preview = { option_id: option.id, label: option.label };
      await transport.play(document, {
        from_bar: set.bar_from,
        loop: { bar_from: set.bar_from, bar_to: set.bar_to },
      });
      notify();
      return { set, option };
    },
    clearPreview,
    startExport(format, barFrom, barTo) {
      return jobs.start(
        format === 'midi' ? 'midi' : format === 'mp3' ? 'mp3' : 'wav',
        async (job) => {
          const result = await runExport(format, barFrom, barTo, job.signal, job.setProgress);
          return result;
        },
      );
    },
    exportResult(jobId) {
      const job = jobs.get<ExportResult>(jobId);
      if (job?.state !== 'completed' || !job.result) return exports.get(jobId) ?? null;
      exports.set(jobId, job.result);
      return job.result;
    },
    async importFile(file) {
      await activate();
      const context = audio.requireRunning();
      const result = await importAudioFile(file, context, {
        id: makeId('take'),
        bpm: store.getDocument().bpm,
      });
      if (!result.ok) {
        throw new ToolError('INVALID_ARGUMENT', result.message, true);
      }
      return result.data;
    },
    loadExample() {
      replaceSong(loadExampleSong(), 'Loaded the example song');
    },
    newSong() {
      replaceSong(createEmptySong(), 'Started a new song');
    },
    stateContext() {
      const audioSnapshot = audio.getSnapshot();
      const transportSnapshot = transport.getSnapshot();
      const microphone = options.environment?.get().permissions.microphone ?? 'unknown';
      return {
        instrument_names: INSTRUMENT_CATALOGUE.map(({ id }) => id),
        transport: {
          playing: transportSnapshot.playing,
          position_bar: transportSnapshot.position_bar,
          ...(transportSnapshot.loop
            ? {
                loop: [transportSnapshot.loop.bar_from, transportSnapshot.loop.bar_to] as [
                  number,
                  number,
                ],
              }
            : {}),
        },
        audio: {
          state: audioSnapshot.state === 'running' ? 'running' : 'locked',
          microphone,
        },
        jobs: jobs
          .list()
          .filter(({ state }) => state === 'queued' || state === 'running')
          .map(({ id, kind, state, progress_pct }) => ({ id, kind, state, progress_pct })),
      };
    },
    getSnapshot() {
      snapshot ??= {
        audio: {
          state: audio.getSnapshot().state,
          running: audio.getSnapshot().state === 'running',
        },
        playing: transport.getSnapshot().playing,
        recording: recorder.getSnapshot(),
        takeBacking,
        jobs: jobs.list(),
        preview,
        loading: reconciler?.getSnapshot().loading ?? {},
        fallbacks: reconciler?.getSnapshot().fallbacks ?? {},
      };
      return snapshot;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    dispose() {
      for (const { id, state } of jobs.list()) {
        if (state === 'queued' || state === 'running') jobs.cancel(id);
      }
      stopJobs();
      stopRecorder();
      stopAudio();
      stopReconciler?.();
      stopReconciler = null;
      persistence?.dispose();
      playback.dispose();
      reconciler?.dispose();
      keyboardInstrument?.dispose();
      metronome.dispose();
      recorder.dispose();
      void transport.stop().catch(() => undefined);
      void audio.close().catch(() => undefined);
      for (const { download_url } of exports.values()) revokeObjectUrl(download_url);
      exports.clear();
      listeners.clear();
    },
  };
}
